import { STORAGE_KEYS } from '../constants';

/**
 * Get admin email and create safe key
 */
const getAdminEmailKey = () => {
  if (typeof window === 'undefined') return 'default';
  const adminEmail = localStorage.getItem(STORAGE_KEYS.ADMIN_EMAIL) || 'default';
  return adminEmail.replace(/@/g, '_').replace(/\./g, '_');
};

/**
 * Get user preference helper
 */
const getUserPreference = (key, defaultValue) => {
  if (typeof window === 'undefined') return defaultValue;
  const stored = localStorage.getItem(key);
  if (stored === null) return defaultValue;
  if (typeof defaultValue === 'boolean') return stored === 'true';
  if (typeof defaultValue === 'number') {
    const num = parseInt(stored);
    return isNaN(num) ? defaultValue : num;
  }
  return stored;
};

/**
 * Format date based on user preference
 * @param {Date|string} date - Date to format
 * @returns {string} - Formatted date string
 */
export const formatDate = (date) => {
  if (!date) return '';
  
  const dateObj = date instanceof Date ? date : new Date(date);
  if (isNaN(dateObj.getTime())) return '';
  
  const format = getUserPreference(STORAGE_KEYS.DATE_FORMAT, 'MM/DD/YYYY');
  
  const day = String(dateObj.getDate()).padStart(2, '0');
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const year = dateObj.getFullYear();
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  
  switch (format) {
    case 'DD/MM/YYYY':
      return `${day}/${month}/${year}`;
    case 'YYYY-MM-DD':
      return `${year}-${month}-${day}`;
    case 'MMM DD, YYYY':
      return `${monthNames[dateObj.getMonth()]} ${day}, ${year}`;
    case 'DD MMM YYYY':
      return `${day} ${monthNames[dateObj.getMonth()]} ${year}`;
    case 'MM/DD/YYYY':
    default:
      return `${month}/${day}/${year}`;
  }
};

/**
 * Format currency based on user preference
 * @param {number} amount - Amount to format
 * @param {string} [currency] - Currency code (optional, uses preference if not provided)
 * @returns {string} - Formatted currency string
 */
export const formatCurrency = (amount, currency = null) => {
  if (amount === null || amount === undefined || isNaN(amount)) return '$0.00';
  
  const currencyCode = currency || getUserPreference(STORAGE_KEYS.CURRENCY_FORMAT, 'USD');
  
  const currencySymbols = {
    USD: '$',
    EUR: '€',
    GBP: '£',
    JPY: '¥'
  };
  
  const symbol = currencySymbols[currencyCode] || '$';
  
  // For JPY, don't show decimals
  if (currencyCode === 'JPY') {
    return `${symbol}${Math.round(amount).toLocaleString()}`;
  }
  
  return `${symbol}${amount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
};

/**
 * Format number based on user preference
 * @param {number} num - Number to format
 * @returns {string} - Formatted number string
 */
export const formatNumber = (num) => {
  if (num === null || num === undefined || isNaN(num)) return '0';
  
  const format = getUserPreference(STORAGE_KEYS.NUMBER_FORMAT, 'standard');
  
  switch (format) {
    case 'compact':
      if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
      if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
      return num.toString();
    case 'scientific':
      return num.toExponential(2);
    case 'standard':
    default:
      return num.toLocaleString();
  }
};

/**
 * Get table items per page preference
 * @returns {number} - Items per page
 */
export const getTableItemsPerPage = () => {
  return getUserPreference(STORAGE_KEYS.TABLE_ITEMS_PER_PAGE, 10);
};

/**
 * Check if tooltips should be shown
 * @returns {boolean} - Whether to show tooltips
 */
export const shouldShowTooltips = () => {
  return getUserPreference(STORAGE_KEYS.SHOW_TOOLTIPS, true);
};

/**
 * Check if chart animations should be enabled
 * @returns {boolean} - Whether to enable chart animations
 */
export const shouldAnimateCharts = () => {
  return getUserPreference(STORAGE_KEYS.CHART_ANIMATIONS, true);
};

